import { zodResolver } from "@hookform/resolvers/zod";
import { Trash2 } from "lucide-react";
import { observer, useLocalObservable } from "mobx-react-lite";
import { type FC, type SyntheticEvent, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import {
  BlockWithBorder,
  CustomButton,
  CustomCardBody,
  CustomCardFooter,
  CustomCheckbox,
  CustomLabel,
  ErrorForm,
  FormCardBasic,
  GreenSwitch,
  HeaderWithBack,
  SimpleText,
  Subtitle,
  Tags,
} from "@/components/atoms";
import {
  InputControl,
  RatingSwitchGroup,
  SelectOptionsWithoutValidation,
  TextareaControl,
} from "@/components/molecules";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { MENU_HASH } from "@/config/path";
import { cn } from "@/lib/utils";
import {
  allergic,
  diet,
  flagsList,
  mealTime,
  NutritionFields,
  occasion,
} from "@/modules/mocksData";
import { rootStore } from "@/store/RootStore";
import { commonIconProps20 } from "@/utils/constants";
import { formatPrice } from "@/utils/formatPrice";
import { useAnchorScroll } from "@/utils/hooks/useAnchorScroll";
import { uploadMedia } from "@/utils/uploadMedia";

import { type DishFormType, makeDishFormSchema } from "../BrandFormMenu/types";
import { MediaStore, MediaUploader, UploadedFiles } from "../MediaUploader";
import NutritionElement from "./NutritionElement";

interface NewDishFormProps {
  isWide?: boolean;
}

const NewDishForm: FC<NewDishFormProps> = observer(({ isWide = true }) => {
  useAnchorScroll();
  const menuStore = rootStore.menuStore;
  const data = menuStore.getCurrentDish();
  const [errorMedia, setErrorMedia] = useState<string | null>(null);

  // Price rules follow the menu's currency: JOD and KWD are written with 3
  // decimals, so 2.750 has to be accepted where a Saudi price stops at 2.75.
  const currency = menuStore.currency;
  const dishSchema = useMemo(
    () => makeDishFormSchema(menuStore.priceDecimals),
    [menuStore.priceDecimals]
  );

  const form = useForm<DishFormType>({
    resolver: zodResolver(dishSchema),
    // Seed the price with its trailing zeros, so reopening a saved Jordanian
    // dish shows the 2.750 that was entered rather than 2.75.
    defaultValues: {
      ...data,
      price: formatPrice(data.price, menuStore.priceDecimals),
    },
    mode: "onChange",
    reValidateMode: "onBlur",
  });

  const mediaFiles = data.images?.media.map(media => ({
    id: media.id,
    type: media.type,
    previewUrl: media.variants[0].url,
  }));

  const mediaStore = useLocalObservable(() => new MediaStore(mediaFiles));

  const navigate = useNavigate();
  const {
    control,
    getValues,
    formState: { isValid },
  } = form;

  const saveDish = async () => {
    const media = await uploadMedia(mediaStore.mediaFiles);
    const values = getValues();

    const payload = {
      ...values,
      images: {
        media: media,
      },
    };

    menuStore.addOrUpdateDishToCurrentCategory(payload);
  };

  const onSubmit = async (e: SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();
    await saveDish();
    navigate(-1);
    form.reset();
    mediaStore.resetStore();
    menuStore.clearCurrentDish();
  };

  const saveAndAddNewDish = async () => {
    await saveDish();
    window.scrollTo({ top: 0, behavior: "smooth" });

    setTimeout(() => {
      form.reset();
      mediaStore.resetStore();
      menuStore.clearCurrentDish();
    }, 500);
  };

  const ratings = getValues("ratings");

  const cleanAndBack = () => {
    menuStore.clearCurrentDish();
    navigate(-1);
  };

  const handleDeleteDish = () => {
    menuStore.removeDishFromCategory();
    navigate(-1);
  };

  return (
    <Form {...form}>
      <form data-testid="new-dish-form-body" className="w-full">
        <FormCardBasic classes={cn(isWide ? "w-full" : "max-w-[628px]")}>
          <HeaderWithBack
            linkProps={{
              to: "",
              onClick: () => cleanAndBack(),
            }}
            title="New dish"
            headerClass={cn(!isWide && "mb-5")}
          >
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 rounded-md px-4 py-2 bg-[#F1F2F4]">
                <p className="text-sm">Availability</p>
                <FormField
                  control={control}
                  name="availability"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <GreenSwitch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          data-testid="dish-availability-switcher"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <CustomButton
                variant="light"
                type="button"
                onClick={handleDeleteDish}
                data-testid="new-dish-form-delete-button"
              >
                <Trash2 {...commonIconProps20} />
                <span>Delete</span>
              </CustomButton>
            </div>
          </HeaderWithBack>

          <CustomCardBody classes="pb-8">
            <BlockWithBorder>
              <Subtitle title="Details" id={MENU_HASH.DETAILS.ID} />
              <div className="parent">
                <div className="responsive-fields">
                  <InputControl
                    formControl={control}
                    label="Dish name (English)"
                    nameField="dishNameEnglish"
                    data-testid="new-dish-form-name-en"
                  />
                  <InputControl<DishFormType>
                    formControl={control}
                    label="Dish name (Arabic)"
                    nameField="dishNameArabic"
                    data-testid="new-dish-form-name-ar"
                  />
                </div>
              </div>

              <div className="parent">
                <div className="responsive-fields">
                  <TextareaControl<DishFormType>
                    formControl={control}
                    label="Description (English)"
                    nameField="descriptionEnglish"
                    data-testid="new-dish-form-description-en"
                  />
                  <TextareaControl<DishFormType>
                    formControl={control}
                    label="Description (Arabic)"
                    nameField="descriptionArabic"
                    data-testid="new-dish-form-description-ar"
                  />
                </div>
              </div>

              <div className="mb-0 flex align-end gap-x-5 text-gray-950">
                <InputControl<DishFormType>
                  formControl={control}
                  label="Preparation time (min)"
                  nameField="preparationTime"
                  classedName="mb-0"
                  placeholder="0"
                  data-testid="new-dish-form-prep-time"
                />
                <InputControl<DishFormType>
                  formControl={control}
                  label={`Price${currency ? ` (${currency.symbol})` : ""}`}
                  nameField="price"
                  classedName="mb-0"
                  // Shaped like the currency, so a Jordanian field hints 0.000
                  // and a Saudi one 0.00.
                  placeholder={formatPrice(0, menuStore.priceDecimals)}
                  data-testid="new-dish-form-price"
                />
              </div>
            </BlockWithBorder>

            <BlockWithBorder>
              <Subtitle
                title="Flags and status"
                classes="mb-[8px]"
                id={MENU_HASH.FLAGS.ID}
              />
              <SelectOptionsWithoutValidation
                name="flagsAndStatuses"
                form={control}
                selectList={flagsList}
              />
            </BlockWithBorder>

            <BlockWithBorder>
              <Subtitle title="Meal context" id={MENU_HASH.CONTEXT.ID} />
              <div className="flex flex-col gap-4.5">
                <Tags
                  key={mealTime.title}
                  tagsList={mealTime.list}
                  title={mealTime.title}
                  form={control}
                  name="meal"
                />
                <Tags
                  key={occasion.title}
                  tagsList={occasion.list}
                  title={occasion.title}
                  form={control}
                  name="occasion"
                />
              </div>
            </BlockWithBorder>

            <BlockWithBorder>
              <Subtitle
                title="Taste profile"
                classes="mb-4"
                id={MENU_HASH.TASTE.ID}
              />
              <div className="grid grid-cols-3 gap-x-12 gap-y-5">
                {Object.entries(ratings ?? {}).map(([taste, rating]) => (
                  <FormField
                    key={taste}
                    control={form.control}
                    name="ratings"
                    render={() => (
                      <FormItem>
                        <FormControl>
                          <RatingSwitchGroup
                            taste={taste}
                            rating={rating}
                            form={form}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                ))}
              </div>
            </BlockWithBorder>
            <BlockWithBorder>
              <Subtitle title="Diet & allergies" id={MENU_HASH.DIET.ID} />
              <div className="flex flex-col gap-4.5">
                <Tags
                  key={diet.title}
                  tagsList={diet.list}
                  title={diet.title}
                  form={control}
                  name="dietTags"
                  optional
                />
                <Tags
                  key={allergic.title}
                  tagsList={allergic.list}
                  title={allergic.title}
                  form={control}
                  name="allergenTags"
                  optional
                />
              </div>
            </BlockWithBorder>
            <BlockWithBorder>
              <Subtitle
                title="Nutrition"
                id={MENU_HASH.NUTRITION.ID}
                classes="mb-6"
              />
              <div className="mb-0 flex align-center gap-x-5 text-gray-950">
                <InputControl<DishFormType>
                  formControl={control}
                  label="Calories (kCal)"
                  nameField="calories"
                  placeholder="0"
                  data-testid="new-dish-form-calories"
                />
                <InputControl<DishFormType>
                  formControl={control}
                  label="Walking minutes to burn calories"
                  nameField="burnCaloriesTime"
                  placeholder="0"
                  data-testid="new-dish-form-burn-time"
                />
              </div>

              <div className="parent">
                <ul className="nutrition-list grid grid-cols-2 gap-x-17 gap-y-5 mb-5">
                  {NutritionFields.map(elem => (
                    <NutritionElement
                      key={elem.name}
                      elem={elem}
                      form={control}
                    />
                  ))}
                </ul>
              </div>
              <FormField
                control={control}
                name="saltContent"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 text-sm text-gray-950">
                    <FormControl>
                      <CustomCheckbox
                        name="saltContent"
                        id="saltContent"
                        checked={field.value}
                        onCheckedChange={checked => {
                          field.onChange(checked);
                        }}
                        className="border-gray-950"
                        data-testid="dich-salt-checker"
                      />
                    </FormControl>
                    <CustomLabel
                      htmlFor="saltContent"
                      label="High salt content"
                    />
                  </FormItem>
                )}
              />
            </BlockWithBorder>

            <Subtitle
              title="Photos & videos"
              classes="mb-1"
              id={MENU_HASH.PHOTOS.ID}
            />
            <SimpleText classes="mb-6">
              Photo requirements: use a JPG, GIF, or PNG. Keep it under 5 MB for
              faster loading. Dimensions: Image (700X700px).Video requirement:
              For best results, uploads should be at least 1080p (1920 x 1080
              pixels) in MP4 format. Keep it under 5 MB
            </SimpleText>
            <div className="flex gap-2">
              {mediaStore.mediaFiles.map((media, index) => (
                <UploadedFiles
                  media={media}
                  key={index}
                  index={index}
                  mediaStore={mediaStore}
                />
              ))}
              <MediaUploader
                disabled={mediaStore.isImageLimitReached}
                mediaStore={mediaStore}
                onError={setErrorMedia}
              />
            </div>
            {errorMedia && <ErrorForm message={errorMedia} />}
          </CustomCardBody>
          <CustomCardFooter>
            <CustomButton
              type="button"
              variant="light"
              onClick={cleanAndBack}
              data-testid="new-dish-form-back-button"
            >
              Back
            </CustomButton>
            <div className="flex gap-2">
              <CustomButton
                variant="secondary"
                disabled={!isValid}
                type="button"
                onClick={saveAndAddNewDish}
                data-testid="new-dish-form-save-add-button"
              >
                Save and add new dish
              </CustomButton>
              <CustomButton
                type="button"
                onClick={onSubmit}
                disabled={!isValid}
                data-testid="new-dish-form-save-button"
              >
                Save
              </CustomButton>
            </div>
          </CustomCardFooter>
        </FormCardBasic>
      </form>
    </Form>
  );
});

export default NewDishForm;
